<template>
  <m-pageframe title="{organization-label}: Edit Order Confirmation Form">
    <v-container
      fluid
      style="padding-left: 30px; padding-right: 30px;"
    >
      <widget-editor-layout
        v-if="form"
        ref="widgetLayout"
        :settings_collapsed="left_pane_collapsed"
        :active_tab.sync="active_tab"
        :back_route="back_route"
        back_label="All Order Confirmation Forms"
        :is_published="is_published"
        :is_archived="form.is_archived"
        :can_unarchive="can_unarchive"
        :item_label="form.internal_label"
        :show_qr_code="false"
        :show_copy_url="false"
        archive_dialog_title="Archive Order Confirmation Form"
        :has_draft_changes="has_draft_changes"
        :publish_time="form.publish_time"
        :publish_signature_id="form.publish_signature_id"
        :change_descriptions="edit_status?.change_descriptions || []"
        :validation_warnings="validation_warnings"
        publish_dialog_title="Publish Order Confirmation Form"
        :outstanding_steps_count="outstanding_steps_count"
        include_setup_checklist
        @archive="archiveForm"
        @unarchive="unarchiveForm"
        @publish="handlePublish"
        @discard-changes="handleDiscardChanges"
        @fix-warning="fixWarning"
      >
        <template #publish-agreement="{ publish_agreement_accepted, update_publish_agreement_accepted }">
          <m-legal-agreement
            type="order-confirmation-settings-publication-agreement"
            :organization_id="$route.params.organization_id"
            :organization_label="form ? form.organization_label : ''"
            :value="publish_agreement_accepted"
            @input="update_publish_agreement_accepted"
            ref="publish-agreement"
            class="mb-4"
          />
        </template>

        <template #checklist-items>
          <widget-editor-checklist-item
            step_number="1"
            title="Create a Customized Order Confirmation Form"
            tooltip="Using a template with your branding applied, design a customized form where customers can review and confirm their order details."
            status_color="success"
            item_class="mt-n2"
            is_complete
          />

          <v-divider class="my-1" />

          <widget-editor-checklist-item
            step_number="2"
            title="Publish the Form"
            tooltip="Once you're happy with your form, publish it. You can edit and republish at any time."
            :is_complete="is_published && !has_draft_changes"
            :status_color="publish_step_color"
            :validation_warnings="validation_warnings"
            is_publish_step
            @fix-warning="fixWarning"
          />

          <v-divider class="my-1" />

          <widget-editor-checklist-item
            step_number="3"
            title="Configure Your Automated Message"
            tooltip="Create your customized automated message that includes a link to the order confirmation form, sent to customers after they place an order."
            :is_complete="automated_message_step_complete"
            :status_color="automated_message_step_color"
            :validation_warnings="automated_message_warnings"
            @fix-warning="fixWarning"
          />
        </template>

        <template #general-settings>
          <v-expansion-panels
            class="section-group mb-6"
            v-model="expanded_general"
            multiple
          >
            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-cog"
                title="Settings"
                subtitle="Configure the basic options for your form"
              />
              <v-expansion-panel-content class="pt-4 pb-2">
                <v-row class="mt-1">
                  <v-col cols="12">
                    <v-text-field
                      v-if="form"
                      data-validation-target="formNameInput"
                      v-model="form.internal_label"
                      label="Form Name"
                      hint="This is only used internally and is not visible to customers"
                      outlined
                      persistent-hint
                      dense
                      :rules="[rules.required]"
                      @input="onChange"
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-message-text-outline"
                title="Messaging Settings"
                subtitle="Configure automated message, user notifications, and after submission message"
              />
              <v-expansion-panel-content class="pt-2">
                <v-row class="mt-0 mb-2" data-validation-target="automatedMessage">
                  <v-col cols="12">
                    <div class="d-flex align-center justify-space-between mb-1">
                      <div class="subtitle-2 font-weight-medium">Automated Message</div>
                      
                      <v-btn
                        v-if="automated_message_template"
                        color="primary"
                        text
                        small
                        @click="onClickCreateOrConfigureAutomatedMessage"
                      >
                        <v-icon aria-hidden="true" left small>mdi-cog</v-icon>
                        Settings
                      </v-btn>
                    </div>

                    <template v-if="automated_message_template">
                      <auto-message-summary-card
                        :template="automated_message_template"
                        :show_label="false"
                        :context_order_conf_settings_id="order_confirmation_settings_id"
                        class="mb-4"
                      />

                    </template>

                    <div
                      v-else-if="is_published"
                      class="shaded-light pa-4 mb-4"
                    >
                      <div class="d-flex align-start">
                        <v-icon
                          aria-hidden="true"
                          color="primary"
                          class="mr-3"
                        >
                          mdi-information-outline
                        </v-icon>
                        <div>
                          <div class="subtitle-1 font-weight-medium mb-1">No Automated Message</div>
                          <div class="body-2">
                            To send this form to customers, you'll need to set up an automated message that delivers the unique confirmation link. We'll provide default messaging for you, but you can customize it as needed.
                          </div>
                        </div>
                      </div>
                      <v-btn
                        color="primary"
                        class="ml-auto d-block mt-4"
                        outlined
                        small
                        @click="onClickCreateOrConfigureAutomatedMessage"
                      >
                        <v-icon aria-hidden="true" left small>mdi-plus</v-icon>
                        Create Automated Message
                      </v-btn>
                    </div>

                    <div
                      v-else
                      class="shaded-light pa-4 mb-4"
                    >
                      <div class="d-flex align-start">
                        <v-icon
                          aria-hidden="true"
                          color="primary"
                          class="mr-3"
                        >
                          mdi-information-outline
                        </v-icon>
                        <div>
                          <div class="subtitle-1 font-weight-medium mb-1">Publish the Form to Create an Automated Message</div>
                          <div class="body-2">
                            To send this form to customers, you'll need to set up an automated message that delivers the unique confirmation link. To proceed with creating the automated message, please publish the form first.
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <v-row class="mt-1 mb-7">
                  <v-col
                    cols="12"
                    data-validation-target="notificationPlanSelector"
                  >
                    <select-users
                      v-model="notification_employee_ids"
                      :organization_id="$route.params.organization_id"
                      label="Notify Users When Customer Requests Changes"
                      hint="RedChirp will attempt to simultaneously notify all users that are available"
                      @change="onChange"
                    />
                  </v-col>
                </v-row>

                <v-switch
                  v-model="form.no_double_opt_in_after_submission_message_enabled"
                  label="Send After Submission Message"
                  @change="onChange"
                  class="mb-4"
                />
                <div class="mt-4">
                  <m-edit-select-organization-owned-endpoint
                    ref="sendFromEndpointSelect"
                    data-validation-target="sendFromEndpointSelect"
                    :organization_id="$route.params.organization_id"
                    v-model="form.send_from_endpoint"
                    label="Send messages from"
                    :include_bot_numbers="true"
                    @input="onChange"
                    @validation="handleEndpointValidation"
                  />
                </div>
                <div>
                  <edit-message-patterns-compact
                    label="After submission message"
                    v-model="form.no_double_opt_in_after_submission_message_patterns"
                    context="form-after-submit"
                    :default_value="get_default_no_double_opt_in_after_submission_message_patterns()
                      "
                    @input="onChange"
                    class="mb-4"
                    ref="afterSubmitInput"
                    data-validation-target="afterSubmitInput"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-connection"
                title="Integrations"
                subtitle="Connect with RedChirp lists and external CRM systems"
              />
              <v-expansion-panel-content class="py-4">
                <h2 class="subtitle-1 font-weight-medium mb-3">CRM Integration Settings</h2>
                <div class="px-md-4">
                  <m-compatible-integrations-hint
                    feature-identifier="customers-auto-enrich-from-redchirp"
                    before-link-text="Customer auto-enrichment from order confirmation submissions requires a"
                    link-text="compatible CRM integration"
                    after-link-text="to be enabled for your organization."
                    modal-title="Compatible CRM Integrations"
                    modal-description="Customer enrichment from order confirmation submissions is currently supported for the following CRM integrations:"
                    no-integrations-message="No compatible CRM integrations are currently enabled for your organization."
                    footer-message="More integrations will be added in the future. If your CRM is not listed above, the order confirmation form will still function but customer data will not be automatically synced to your CRM."
                    :show-hint="!customer_auto_enrich_feature_available"
                  />

                  <div
                    v-if="customer_auto_enrich_feature_available"
                    class="rounded-lg grey lighten-5 pa-4 mb-6 border-1"
                    style="border: 1px solid #e0e0e0;"
                  >
                    <div class="d-flex align-center mb-3">
                      <v-icon small color="primary" class="mr-2">mdi-sync</v-icon>
                      <span class="text-subtitle-2 font-weight-bold">How CRM Sync Works for Order Confirmation Forms</span>
                    </div>

                    <div class="d-flex align-start mb-3">
                      <v-icon color="green" small class="mt-1 mr-3">mdi-check-circle-outline</v-icon>
                      <div>
                        <div class="text-caption font-weight-bold text--primary">Updates Email Subscription Only</div>
                        <div class="text-caption text--secondary" style="line-height: 1.4;">
                          Since the customer's contact information originated from your CRM, we treat it as the source of truth. RedChirp only attempts to sync back email subscription status when consent is provided by the customer.
                        </div>
                      </div>
                    </div>

                    <div class="d-flex align-start mb-3">
                      <v-icon color="grey darken-1" small class="mt-1 mr-3">mdi-minus-circle-outline</v-icon>
                      <div>
                        <div class="text-caption font-weight-bold text--primary">No Consent, No Sync</div>
                        <div class="text-caption text--secondary" style="line-height: 1.4;">
                          If the customer does not provide consent, no data should be synced automatically.
                        </div>
                      </div>
                    </div>

                    <div class="d-flex align-start">
                      <v-icon color="orange darken-2" small class="mt-1 mr-3">mdi-shield-edit-outline</v-icon>
                      <div>
                        <div class="text-caption font-weight-bold text--primary">Change Requests are Protected</div>
                        <div class="text-caption text--secondary" style="line-height: 1.4;">
                          If a customer requests changes to their order, nothing should be synced automatically. This prevents modifying CRM data when the order details are in question.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <h2 class="subtitle-1 font-weight-medium">
                  RedChirp Integration Settings
                </h2>
                <div class="px-md-4">
                  <v-select
                    :label="form.double_opt_in_state === 'enabled'
                      ? 'After double opt-in, add contact to list(s)'
                      : 'On submission, add contact to list(s)'
                      "
                    v-model="form.add_to_lists"
                    :items="available_lists"
                    item-text="text"
                    item-value="value"
                    @change="onChange"
                    multiple
                    chips
                    clearable
                    persistent-hint
                    hint="At minimum, you must have a phone number in the form submission to add the customer to a list. Only customers who provide a phone number with consent will be added to lists."
                  >
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>No lists available</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-text"
                title="Agreement"
                subtitle="Manage initial consent gathering and agreement text"
              />
              <v-expansion-panel-content>
                <div class="d-flex align-center mt-4">
                  <h2 class="subtitle-1 font-weight-medium sr-only">Consent Gathering</h2>
                </div>
                <div class="mt-0">
                  <v-card flat>
                    <v-card-title class="text-h6 pl-0 pt-1 pb-2">
                      <v-icon left>mdi-email</v-icon>
                      Email Agreement
                    </v-card-title>
                    <v-card-text>
                      <m-edit-select-enum
                        label="Agreement Type"
                        v-model="form.email_consent_agreement_type"
                        enum_name="consent_agreement_type"
                        dialog_title="Select Agreement Type"
                        @change="onChange"
                      />
                      <div
                        v-if="form.email_consent_agreement_type === 'custom'"
                        class="mb-4"
                      >
                        <edit-message-patterns-compact
                          label="Custom Agreement Patterns"
                          v-model="form.email_consent_agreement_custom_patterns"
                          hint="Custom patterns for email agreement text. Use variables like {organization-label}, {terms-link}, {privacy-policy-link}"
                          :default_value="getDefaultEmailAgreementPattern()
                            ? [getDefaultEmailAgreementPattern()]
                            : null
                            "
                          @input="onChange"
                        />
                      </div>
                      <div
                        v-else
                        class="mb-4"
                      >
                        <v-alert
                          type="info"
                          dense
                          text
                          class="mt-2"
                        >
                          Using default agreement pattern. The resolved text is shown in the
                          preview.
                        </v-alert>
                      </div>
                      <m-edit-select-enum
                        ref="emailConsentModeSelect"
                        data-validation-target="emailConsentModeSelect"
                        label="Consent Gathering Mode"
                        v-model="form.email_consent_gathering_state"
                        enum_name="form_consent_gathering_state"
                        dialog_title="Select Consent Gathering Mode"
                        @change="onEmailConsentGatheringChange"
                        :filter_options="filtered_email_consent_gathering_options"
                      />
                      <m-edit-css
                        label="Email Agreement"
                        v-model="form.email_agreement_style.style"
                        :default_value="form.email_agreement_style.default_style"
                        :mobile_value.sync="form.email_agreement_style.mobile_styles"
                        :default_mobile_value="form.email_agreement_style.default_mobile_styles"
                        class="mb-4"
                      />
                    </v-card-text>
                  </v-card>

                  <v-divider class="mt-3 mb-6"></v-divider>

                  <v-card flat>
                    <v-card-title class="text-h6 pl-0 pt-1 pb-2">
                      <v-icon left>mdi-phone</v-icon>
                      Phone Number Agreement
                    </v-card-title>
                    <v-card-text>
                      <m-edit-select-enum
                        label="Agreement Type"
                        v-model="form.phone_number_consent_agreement_type"
                        enum_name="consent_agreement_type"
                        dialog_title="Select Agreement Type"
                        @change="onChange"
                      />
                      <div
                        v-if="form.phone_number_consent_agreement_type === 'custom'"
                        class="mb-4"
                      >
                        <edit-message-patterns-compact
                          label="Custom Agreement Patterns"
                          v-model="form.phone_number_consent_agreement_custom_patterns"
                          hint="Custom patterns for phone agreement text. Use variables like {organization-label}, {terms-link}, {privacy-policy-link}"
                          :default_value="getDefaultPhoneAgreementPattern()
                            ? [getDefaultPhoneAgreementPattern()]
                            : null
                            "
                          @input="onChange"
                        />
                      </div>
                      <div
                        v-else
                        class="mb-4"
                      >
                        <v-alert
                          type="info"
                          dense
                          text
                          class="mt-2"
                        >
                          Using default agreement pattern. The resolved text is shown in the
                          preview.
                        </v-alert>
                      </div>
                      <m-edit-select-enum
                        ref="phoneConsentModeSelect"
                        data-validation-target="phoneConsentModeSelect"
                        label="Consent Gathering Mode"
                        v-model="form.phone_number_consent_gathering_state"
                        enum_name="form_consent_gathering_state"
                        dialog_title="Select Consent Gathering Mode"
                        @change="onPhoneConsentGatheringChange"
                        :filter_options="filtered_phone_consent_gathering_options"
                      />
                      <m-edit-css
                        label="Phone Number Agreement"
                        v-model="form.phone_number_agreement_style.style"
                        :default_value="form.phone_number_agreement_style.default_style"
                        :mobile_value.sync="form.phone_number_agreement_style.mobile_styles"
                        :default_mobile_value="form.phone_number_agreement_style.default_mobile_styles
                          "
                        class="mb-4"
                      />
                    </v-card-text>
                  </v-card>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>

        <template #content-styles>
          <v-expansion-panels
            v-if="form"
            v-model="expanded_styles"
            multiple
            class="section-group mb-6"
          >
            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-view-dashboard"
                title="Container & Layout"
                subtitle="Configure form layout, container styling, and background effects"
              />
              <v-expansion-panel-content class="pt-4">
                <div class="px-md-4">                  
                  <widget-editor-reapply-template
                    class="mt-3 mb-6"
                    :form_id="$route.params.order_confirmation_settings_id"
                    template_selection_route="order-confirmation-template-selection"
                  />

                  <m-edit-css
                    label="Container"
                    :subheader="is_side_by_side_layout ? 'The wrapper that holds all content, including the image and form' : 'The main form container that holds all content and input fields'"
                    v-model="form.container.style"
                    :default_value="form.container.default_style"
                    :mobile_value.sync="form.container.mobile_styles"
                    :default_mobile_value="form.container.default_mobile_styles"
                    class="mb-4"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                  <m-edit-css
                    v-if="is_side_by_side_layout"
                    label="Container Content"
                    :subheader="`The inner content area that contains the form ${is_side_by_side_layout ? '(the right panel on larger screens)' : ''}`"
                    v-model="form.container_content.style"
                    :default_value="form.container_content.default_style"
                    :mobile_value.sync="form.container_content.mobile_styles"
                    :default_mobile_value="form.container_content.default_mobile_styles"
                    class="mb-4"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                  <m-edit-css
                    label="Form Containers"
                    subheader="The elements containing the order data on the initial order confirmation screen"
                    v-model="form.form_containers.style"
                    :default_value="form.form_containers.default_style"
                    :mobile_value.sync="form.form_containers.mobile_styles"
                    :default_mobile_value="form.form_containers.default_mobile_styles"
                    hint="Styling for form input containers and sections"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-format-header-1"
                title="Header & Content"
                subtitle="Customize header text and content lines"
              />
              <v-expansion-panel-content class="mt-6 pt-2 pb-0">
                <div class="px-md-4">
                  <m-edit-styled-text
                    label="Initial Title Text"
                    v-model="form.initial_title_text"
                    hint="Main title text displayed on the order confirmation form"
                    @change="onChange"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    label="Initial Subtitle Text"
                    v-model="form.initial_subtitle_text"
                    hint="Subtitle text displayed below the main title"
                    @change="onChange"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-image-multiple"
                title="Images & Media"
                subtitle="Configure logo display and styling"
              />
              <v-expansion-panel-content class="pt-2">
                <div class="mt-4 px-md-4">
                  <v-switch
                    v-model="form.use_logo"
                    label="Show Logo"
                    color="primary"
                    class="mb-4"
                    hide-details
                    @change="onChange"
                  />
                  <transition name="fade">
                    <div
                      v-if="form.use_logo"
                    >
                      <div class="sr-only">Logo Image</div>
                      <m-image-selector
                        ref="logoImageInput"
                        data-validation-target="logoImageInput"
                        v-model="form.logo_image.url"
                        :organization_id="form.organization_id"
                        label="Logo Image"
                        @input="onChange"
                      />
                      <m-edit-css
                        label="Logo Image"
                        v-model="form.logo_image.style"
                        :default_value="form.logo_image.default_style"
                        :mobile_value.sync="form.logo_image.mobile_styles"
                        :default_mobile_value="form.logo_image.default_mobile_styles"
                        @input="onChange"
                        @mobileValueUpdated="onChange"
                      />
                    </div>
                  </transition>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-button-cursor"
                title="Buttons & Actions"
                subtitle="Style confirmation buttons and action elements"
              />
              <v-expansion-panel-content class="pt-8 pb-4">
                <div class="px-md-4">
                  <m-edit-styled-text
                    label="Check All Button Text"
                    v-model="form.check_all_button_text"
                    hint="Text displayed on the button to check all items"
                    @change="onChange"
                  />
                  <m-edit-css
                    label="Check All Button"
                    class="mb-12"
                    v-model="form.check_all_button.style"
                    :default_value="form.check_all_button.default_style"
                    :mobile_value.sync="form.check_all_button.mobile_styles"
                    :default_mobile_value="form.check_all_button.default_mobile_styles"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />

                  <m-edit-styled-text
                    label="Confirm Button Text"
                    v-model="form.confirm_button_text"
                    hint="Text displayed on the main confirmation button"
                    @change="onChange"
                  />
                  <m-edit-css
                    label="Confirm Button"
                    class="mb-12"
                    v-model="form.confirm_button.style"
                    :default_value="form.confirm_button.default_style"
                    :mobile_value.sync="form.confirm_button.mobile_styles"
                    :default_mobile_value="form.confirm_button.default_mobile_styles"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />

                  <m-edit-styled-text
                    label="Request Changes Button Text"
                    v-model="form.update_order_button_text"
                    hint="Text displayed on the order update request button"
                    @change="onChange"
                    class="mb-4"
                  />
                  <m-edit-css
                    label="Request Changes Button"
                    class="mb-12"
                    v-model="form.update_order_button.style"
                    :default_value="form.update_order_button.default_style"
                    :mobile_value.sync="form.update_order_button.mobile_styles"
                    :default_mobile_value="form.update_order_button.default_mobile_styles"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />

                  <m-edit-styled-text
                    label="Back to Confirmation Button Text"
                    v-model="form.back_to_confirmation_button_text"
                    hint="Text displayed on the button to go back to the confirmation page"
                    @change="onChange"
                  />
                  <m-edit-css
                    label="Back to Confirmation Button"
                    class="mb-12"
                    v-model="form.back_to_confirmation_button.style"
                    :default_value="form.back_to_confirmation_button.default_style"
                    :mobile_value.sync="form.back_to_confirmation_button.mobile_styles"
                    :default_mobile_value="form.back_to_confirmation_button.default_mobile_styles"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />

                  <m-edit-styled-text
                    label="Submit Changes Button Text"
                    v-model="form.submit_changes_button_text"
                    hint="Text displayed on the button to submit requested changes"
                    @change="onChange"
                  />
                  <m-edit-css
                    label="Submit Changes Button"
                    v-model="form.submit_changes_button.style"
                    :default_value="form.submit_changes_button.default_style"
                    :mobile_value.sync="form.submit_changes_button.mobile_styles"
                    :default_mobile_value="form.submit_changes_button.default_mobile_styles"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-pencil-box"
                title="Update Order Section"
                subtitle="Configure the order update request section content"
              />
              <v-expansion-panel-content class="pt-2">
                <div class="mt-5 px-md-4">
                  <m-edit-styled-text
                    label="Update Order Title"
                    v-model="form.update_order_title"
                    hint="Title text for the order update section"
                    class="mb-4"
                    @change="onChange"
                  />
                  <m-edit-styled-text
                    label="Update Order Subtitle"
                    v-model="form.update_order_subtitle"
                    hint="Subtitle text explaining the order update process"
                    class="mb-4"
                    @change="onChange"
                  />
                  <m-edit-styled-text
                    label="Update Order Disclaimer"
                    v-model="form.update_order_disclaimer"
                    hint="Disclaimer text for the order update section"
                    @change="onChange"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-check-circle"
                title="Thank You Page"
                subtitle="Customize the confirmation success page"
              />
              <v-expansion-panel-content class="pt-2">
                <div class="mt-4 px-md-4">
                  <h2 class="subtitle-1 font-weight-medium mb-4">No Changes Requested</h2>
                  <m-edit-styled-text
                    label="Thank You Title (No Changes)"
                    v-model="form.thank_you_no_changes_title"
                    hint="Title shown when customer confirms order without requesting changes"
                    @change="onChange"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    label="Thank You Subtitle (No Changes)"
                    v-model="form.thank_you_no_changes_subtitle"
                    hint="Subtitle shown when customer confirms order without requesting changes"
                    @change="onChange"
                    class="mb-4"
                  />

                  <h2 class="subtitle-1 font-weight-medium mb-4 mt-6">Changes Requested</h2>
                  <m-edit-styled-text
                    label="Thank You Title (Changes Requested)"
                    v-model="form.thank_you_changes_requested_title"
                    hint="Title shown when customer requests order changes"
                    @change="onChange"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    label="Thank You Subtitle (Changes Requested)"
                    v-model="form.thank_you_changes_requested_subtitle"
                    hint="Subtitle shown when customer requests order changes"
                    @change="onChange"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    label="Container Text (Changes Requested)"
                    v-model="form.thank_you_changes_requested_container_text"
                    hint="Container text explaining the change request process"
                    @change="onChange"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    label="Response Text (Changes Requested)"
                    v-model="form.thank_you_changes_requested_response_text"
                    hint="Text explaining how the business will respond to change requests"
                    @change="onChange"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    ref="contactTextChangesRequestedInput"
                    data-validation-target="contactTextChangesRequestedInput"
                    label="Contact Text (Changes Requested)"
                    v-model="form.thank_you_changes_requested_contact_text"
                    hint="Contact information for customers with questions about changes"
                    @change="onChange"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-account-check"
                title="Age Verification"
                subtitle="Style age verification elements"
              />
              <v-expansion-panel-content class="pt-2">
                <div class="mt-2 px-md-4">
                  <v-row>
                    <v-col cols="12">
                      <v-checkbox
                        v-model="form.age_verification_enabled"
                        label="Enable Age Verification"
                        hint="Require customers to verify they are of legal age"
                        persistent-hint
                        hide-details="auto"
                        class="mt-0"
                        @change="onChange"
                      />
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <m-edit-styled-text
                        label="Age Verification Message"
                        v-model="form.age_verification_message"
                        class="mt-1"
                        @change="onChange"
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>

        <template #preview>
          <widget-preview-panel
            ref="previewPanel"
            :preview_mode="preview_mode"
            :is_fullscreen="left_pane_collapsed"
            :is_loading="is_preview_loading"
            :dimensions="preview_dimensions"
            :initial_size_mode="use_actual_size"
            @fullscreen-toggle="left_pane_collapsed = !left_pane_collapsed"
            @size-mode-change="use_actual_size = $event"
          >
            <template #floating-controls>
              <widget-editor-floating-preview-control
                :visible="floating_control_visible"
                :preview_mode="preview_mode"
                :current_page="current_page"
                :pages="widget_pages"
                :view_modes="['mobile', 'tablet', 'tablet-landscape', 'desktop']"
                supports_test_data
                @preview-mode-change="onPreviewModeChange"
                @page-change="onPageChange"
                @auto-fill-test-data="() => onAutoFillTestData()"
                @dismiss="onDismissFloatingControl"
              />
            </template>

            <template #preview>
              <iframe
                v-if="form && form.order_confirmation_settings_publication_uuid"
                :key="preview_nonce"
                :src="previewSrc"
                class="preview-iframe"
                frameborder="0"
                @load="onPreviewLoad"
                ref="preview-iframe"
              />
              <div
                v-else
                class="preview-placeholder"
              >
                <v-icon
                  size="48"
                  color="grey lighten-1"
                >mdi-form-select</v-icon>
                <div class="text-caption text--secondary mt-2">
                  Save form to see preview
                </div>
              </div>
            </template>
          </widget-preview-panel>
        </template>
      </widget-editor-layout>
    </v-container>

    <!-- Automated Message Settings Dialog -->
    <v-dialog
      v-model="show_automated_message_dialog"
      max-width="900px"
      scrollable
    >
      <v-card>
        <v-card-title class="text-h5">
          Automated Message Settings
          <v-spacer />

          <v-btn
            icon
            aria-label="Close dialog"
            @click="show_automated_message_dialog = false"
          >
            <v-icon aria-hidden="true">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <auto-message-template-settings
            v-if="automated_message_template"
            :key="message_template_key"
            :template_context="automated_message_template.template_context || 'order-placed-request-order-confirmation'"
            :template_subject_id="automated_message_template.template_subject_id || '00000000-0000-0000-0000-000000000000'"
            :order_conf_form_id="order_confirmation_settings_id"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="primary"
            outlined
            @click="show_automated_message_dialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <m-auto-message-create-new-dialog
      ref="create-new-dialog"
      :organization_id="$route.params.organization_id"
      @confirm="onConfirmNewTemplate"
    />

  </m-pageframe>
</template>

<script>
import WidgetEditorLayout from '../../components/widget/widget-editor-layout.vue';
import WidgetPreviewPanel from '../../components/widget/widget-preview-panel.vue';
import WidgetEditorFloatingPreviewControl from '../../components/widget/widget-editor-floating-preview-control.vue';
import WidgetEditorReapplyTemplate from '../../components/widget/widget-editor-reapply-template.vue';
import WidgetEditorChecklistItem from '../../components/widget/widget-editor-checklist-item.vue';
import ExpansionPanelHeaderRich from '../../components/shared/expansion-panels/expansion-panel-header-rich.vue';
import EditMessagePatternsCompact from '@/components/input/edit-message-patterns-compact.vue';
import MEditCss from '@/components/input/m-edit-css.vue';
import mEditStyledText from '@/components/input/m-edit-styled-text.vue';
import MImageSelector from '@/components/input/image-selector.vue';
import SelectUsers from '@/components/input/select-users.vue';
import AutoMessageTemplateSettings from '@/views/auto-messages/auto-message-template-settings.vue';
import AutoMessageSummaryCard from '@/components/auto-messages/auto-message-summary-card.vue';
import MAutoMessageCreateNewDialog from '@/views/auto-messages/auto-message-create-new-dialog.vue';
import { composeOrderConfirmationPreviewParams, sendPageChangeToWidget, sendSettingsToWidget, sendAutoFillTestData } from '@/js/widget/order-confirmation-preview-composer.js';
import { buildWidgetUrl } from '@/js/widget/widget-url-builder.js';
import { WIDGET_TO_PARENT } from '@/js/widget/widget-messaging.js';
import GuidedValidationMixin from '@/mixins/guided-validation.js';
import WidgetEditorStatePersistence from '@/mixins/widget/widget-editor-state-persistence.js';
import WidgetEditorCore from '@/mixins/widget/widget-editor-core.js';

export default {
  name: 'OrderConfirmationEdit',
  mixins: [GuidedValidationMixin, WidgetEditorStatePersistence, WidgetEditorCore],

  components: {
    WidgetEditorLayout,
    WidgetPreviewPanel,
    WidgetEditorFloatingPreviewControl,
    WidgetEditorReapplyTemplate,
    WidgetEditorChecklistItem,
    ExpansionPanelHeaderRich,
    EditMessagePatternsCompact,
    MEditCss,
    mEditStyledText,
    MImageSelector,
    SelectUsers,
    AutoMessageTemplateSettings,
    AutoMessageSummaryCard,
    MAutoMessageCreateNewDialog,
  },

  // Note: mounted() and beforeUnmount() are handled by widget-editor-core mixin

  data() {
    return {
      // core mixin overrides
      current_page: 'order-confirmation',
      preview_mode: 'mobile', // 'mobile', 'tablet', 'tablet-landscape', 'desktop'
      
      // editor persistence mixin overrides
      editor_state_key: this.$route.params.order_confirmation_settings_id,

      
      // widget-specific data
      first_load_complete: false,
      order_confirmation_settings_id: this.$route.params.order_confirmation_settings_id,
      order_confirmation_id: this.$route.params.order_confirmation_id,
      widget_pages: [
        { value: 'order-confirmation', label: 'Order Confirmation', icon: 'mdi-form-select' },
        { value: 'request-changes', label: 'Request Changes', icon: 'mdi-pencil-box' },
        { value: 'thanks-no-changes', label: 'Thank You (No Changes)', icon: 'mdi-heart-minus' },
        { value: 'thanks-with-changes', label: 'Thank You (With Changes)', icon: 'mdi-heart-plus' },
      ],
      rules: {
        required: value => !!value || 'This field is required',
      },

      // order confirmation specific
      automated_message_template: null,
      automated_message_template_error: false,
      show_automated_message_dialog: false,
      uncreated_templates: null,
      message_template_key: 0,
      has_necessary_integration: false,
    };
  },

  computed: {
    is_side_by_side_layout() {
      return this.form?.layout_mode === 'side-by-side' || false;
    },

    back_route() {
      return {
        name: 'search-forms-order-confirmation',
        params: { organization_id: this.$route.params.organization_id },
      };
    },

    has_archived_published_form() {
      return this.edit_status?.published_form?.is_archived === true;
    },

    has_unarchived_draft_with_archived_public_form() {
      if (!this.form) return false;
      return !this.form.is_archived && this.has_archived_published_form;
    },

    form_archived_everywhere() {
      if (!this.form) return false;
      return this.form.is_archived && this.has_archived_published_form;
    },

    automated_message_exists_but_not_linked() {
      return this.automated_message_template &&
        (!this.automated_message_template.order_confirmation_settings_id ||
          this.automated_message_template.order_confirmation_settings_id !== this.order_confirmation_settings_id);
    },

    automated_message_does_not_exist() {
      return this.automated_message_template_error || !this.automated_message_template;
    },

    automated_message_complete() {
      return !!this.automated_message_template;
    },

    automated_message_linked_to_current_form() {
      if (!this.automated_message_template) return false;
      return this.automated_message_template.order_confirmation_settings_id === this.order_confirmation_settings_id;
    },

    automated_message_requires_publication_warning() {
      return this.automated_message_linked_to_current_form && this.has_unarchived_draft_with_archived_public_form;
    },

    automated_message_requires_archived_warning() {
      return this.automated_message_linked_to_current_form && this.form_archived_everywhere;
    },

    automated_message_warning_message() {
      if (this.automated_message_requires_publication_warning) {
        return 'Your published form is archived but your draft is not. Publish your draft to unarchive the published version so that automated messages can use it.';
      }
      if (this.automated_message_requires_archived_warning) {
        return 'This form is archived but is still linked to your automated message. Unarchive it or update the automated message to use an active form.';
      }
      return '';
    },

    automated_message_step_complete() {
      if (!this.automated_message_complete) return false;
      if (this.automated_message_exists_but_not_linked) return false;
      if (this.automated_message_requires_publication_warning) return false;
      if (this.automated_message_requires_archived_warning) return false;
      return true;
    },

    publish_step_color() {
      if (!this.is_published || this.validation_warnings.length > 0) return 'grey';
      return this.has_draft_changes ? 'orange' : 'success';
    },

    automated_message_step_color() {
      return this.automated_message_step_complete ? 'success' : 'grey';
    },

    automated_message_warnings() {
      if (!this.is_published) return [];

      const warnings = [];

      if (this.automated_message_warning_message) {
        warnings.push(
          this.mixinCreateWarning(
            this.automated_message_warning_message,
            { tab: 0, panel: 1, target: 'automatedMessage' }
          )
        );
      }
      
      if (this.automated_message_exists_but_not_linked) {
        warnings.push(
          this.mixinCreateWarning(
            `Your message is ${this.automated_message_template.order_confirmation_settings_id ? 'currently using a different form' : 'not configured to use this form'}.`,
            {
              action: () => this.linkAutomatedMessage(),
              action_button_text: this.automated_message_template.order_confirmation_settings_id ? 'Use this form instead' : 'Use this form',
            }
          )
        );
        return warnings;
      }
      
      if (!this.automated_message_complete) {
        warnings.push(
          this.mixinCreateWarning(
            'Configure your automated message to send order confirmation links.',
            { tab: 0, panel: 1, target: 'automatedMessage' }
          )
        );
      }
      
      return warnings;
    },

    outstanding_steps_count() {
      let count = 0;
      if (!this.is_published || this.has_draft_changes) {
        count++;
      }
      if (!this.automated_message_step_complete) {
        count++;
      }
      return count;
    },

    public_url() {
      if (!this.edit_status?.has_published_version) {
        return '';
      }
      if (!this.form?.short_id) return '';
      return `${window.location.origin}/app/order-confirmation/${this.form.short_id}`;
    },

    publicUrlHint() {
      if (!this.is_published) {
        return 'URL will be available after publishing';
      }
      return 'This is the public URL customers will use';
    },

    widget_props() {
      if (!this.form) return {};

      return {
        settings: this.form,
        is_test_submission: true,
        preview_mode: this.preview_mode,
      };
    },

    preview_dimensions() {
      const dimensions = {
        mobile: { width: 410, height: 844 },
        tablet: { width: 1024, height: 1366 },
        'tablet-landscape': { width: 1366, height: 1024 },
        desktop: { width: 1920, height: 1080 },
      };
      return dimensions[this.preview_mode] || dimensions['tablet-landscape'];
    },

    previewSrc() {

      if (!this.form || !this.form.order_confirmation_settings_publication_uuid) {

        return null;
      }

      const params = {};
      if (this.preview_mode === 'mobile') {
        params.mobile = 'true';
      }

      const url = buildWidgetUrl('order-confirmation', params, 5176);
      return url;
    },

    filtered_email_consent_gathering_options() {
      //Email can choose all
      return ['consent-via-field-submission', 'checkbox-consent', 'none'];
    },

    filtered_phone_consent_gathering_options() {

      return ['checkbox-consent', 'none'];
    },

    notification_employee_ids: {
      get() {
        if (!this.form || !this.form.update_order_notification_plan) {
          return [];
        }
        return this.extractEmployeeIdsFromNotificationPlan(this.form.update_order_notification_plan);
      },
      set(employee_ids) {
        if (!this.form || !this.form.update_order_notification_plan) {
          return;
        }
        this.updateNotificationPlanEmployees(employee_ids);
      },
    },
  },

  watch: {
    form: {
      handler() {
        this.onChange();
      },
      deep: true,
    },

    show_automated_message_dialog(new_val, old_val) {
      if (old_val === true && new_val === false) {
        setTimeout(() => {
          this.getOrderConfAutomatedMessage();
        }, 750);
        this.onChange();
      }
    },
  },

  methods: {
    // ============ Required overrides for widget-editor-core mixin ============
    getLoadEndpoint() {
      return 'GET: /api/v1/order-confirmation/settings/:order_confirmation_settings_id/edit-status';
    },

    getLoadParams() {
      return {
        organization_id: this.$route.params.organization_id,
        order_confirmation_settings_id: this.$route.params.order_confirmation_settings_id,
      };
    },

    getSaveEndpoint() {
      return 'PUT: /api/v1/order-confirmation/settings';
    },

    getPublishEndpoint() {
      return 'PUT: /api/v1/order-confirmation/settings/:order_confirmation_settings_id/publish';
    },

    getPublishParams() {
      return {
        order_confirmation_settings_id: this.order_confirmation_settings_id,
      };
    },

    getDiscardChangesEndpoint() {
      return 'PUT: /api/v1/order-confirmation/settings/:order_confirmation_settings_id/discard-changes';
    },

    getDiscardParams() {
      return {
        order_confirmation_settings_id: this.order_confirmation_settings_id,
      };
    },

    getArchiveEndpoint() {
      return 'PUT: /api/v1/order-confirmation/settings/:order_confirmation_settings_id/archive';
    },

    getArchiveParams() {
      return {
        order_confirmation_settings_id: this.order_confirmation_settings_id,
      };
    },

    getUnarchiveEndpoint() {
      return 'PUT: /api/v1/order-confirmation/settings/:order_confirmation_settings_id/unarchive';
    },

    getUnarchiveParams() {
      return {
        order_confirmation_settings_id: this.order_confirmation_settings_id,
      };
    },

    composePreviewParams() {
      return composeOrderConfirmationPreviewParams({
        settings: this.form,
        is_test_submission: true,
        preview_mode: this.preview_mode,
      });
    },

    sendToWidget(settings) {
      sendSettingsToWidget(this.$refs['preview-iframe'], settings);
    },

    // ============ Optional overrides for widget-editor-core mixin ============
    onWidgetInitialized() {
      this.loadDefaultLegalPatterns();
      this.getOrderConfAutomatedMessage();
      this.loadIntegrationData();
    },

    onPublishSuccess() {
      this.$nextTick(() => {
        this.suggestAutomatedMessageSetup();
      });
    },

    getCustomMessageHandlers() {
      return {
        [WIDGET_TO_PARENT.READY]: () => {
          const should_recycle_test_data = this.first_load_complete;
          this.onAutoFillTestData(should_recycle_test_data);

          if (!this.first_load_complete) {
            this.first_load_complete = true;
          }
        },
      };
    },

    getRouteLeaveExceptions() {
      return [
        'order-confirmation-template-selection',
        'auto-messages-settings',
        'auto-messages-template-settings'
      ];
    },

    // ============ Widget-specific methods ============
    loadIntegrationData() {
      this.$rc.hasEnabledIntegrationFeature(
        'order-confirmation',
        is_supported => {
          this.has_necessary_integration = is_supported;
        },
        () => {
          this.has_necessary_integration = false;
        }
      );
    },

    extractEmployeeIdsFromNotificationPlan(notification_plan) {
      if (!notification_plan || !notification_plan.stages || notification_plan.stages.length === 0) {
        return [];
      }

      const first_stage = notification_plan.stages[0];
      if (!first_stage || !first_stage.step || !first_stage.step.action) {
        return [];
      }

      const action = first_stage.step.action;
      if (!action.employee_selector || !action.employee_selector.selected_object_ids) {
        return [];
      }

      return action.employee_selector.selected_object_ids;
    },

    updateNotificationPlanEmployees(employee_ids) {
      const notification_plan = this.form.update_order_notification_plan;
      if (!notification_plan || !notification_plan.stages || notification_plan.stages.length === 0) {
        return;
      }

      const first_stage = notification_plan.stages[0];
      if (!first_stage || !first_stage.step || !first_stage.step.action) {
        return;
      }

      const action = first_stage.step.action;

      if (!action.employee_selector) {
        action.employee_selector = {
          organization_id: notification_plan.organization_id || this.$route.params.organization_id,
          selected_object_ids: [],
        };
      }

      this.$set(action.employee_selector, 'selected_object_ids', employee_ids || []);
    },

    validateForm() {
      this.validation_warnings = [];
      if (!this.form) return;

      if (!this.form.internal_label || this.form.internal_label.trim() === '') {
        this.validation_warnings.push(
          this.mixinCreateWarning('A Form Name is required.', {
            tab: 0,
            panel: 0,
            target: 'formNameInput',
          })
        );
      }

      if (!this.notification_employee_ids || this.notification_employee_ids.length === 0) {
        this.validation_warnings.push(
          this.mixinCreateWarning('At least one user must be selected to receive notifications whenever a customer requests a change to their order.', {
            tab: 0,
            panel: 1,
            target: 'notificationPlanSelector',
          })
        );
      }

      if (this.form.no_double_opt_in_after_submission_message_enabled && !this.form.send_from_endpoint) {
        this.validation_warnings.push(
          this.mixinCreateWarning('A "Send messages from" number is required for messaging.', {
            tab: 0,
            panel: 1,
            target: 'sendFromEndpointSelect',
          })
        );
      }

      if (
        this.form.no_double_opt_in_after_submission_message_enabled &&
        this.form.no_double_opt_in_after_submission_message_patterns.length
      ) {
        if (
          this.form.no_double_opt_in_after_submission_message_patterns.every(
            pattern => !pattern || pattern.trim().length === 0
          )
        ) {
          this.validation_warnings.push(
            this.mixinCreateWarning('After submission message is required.', {
              tab: 0,
              panel: 1,
              target: 'afterSubmitInput',
            })
          );
        }
      }

      if (this.endpoint_validation_error) {
        this.validation_warnings.push(
          this.mixinCreateWarning(this.endpoint_validation_error, {
            tab: 0,
            panel: 1,
            target: 'sendFromEndpointSelect',
          })
        );
      }

      if (this.form.use_logo && !this.form.logo_image.url) {
        this.validation_warnings.push(
          this.mixinCreateWarning('Logo is enabled, but no logo image is set.', {
            tab: 1,
            panel: 2,
            target: 'logoImageInput',
          })
        );
      }

      if (
        this.form.thank_you_changes_requested_contact_text &&
        this.form.thank_you_changes_requested_contact_text.text &&
        this.form.thank_you_changes_requested_contact_text.text.includes('{enter-contact-info}')
      ) {
        this.validation_warnings.push(
          this.mixinCreateWarning(
            "Please replace the placeholder contact information for change requests.",
            {
              tab: 1, // Content & Styles tab
              panel: 5, // Thank You Page panel
              target: 'contactTextChangesRequestedInput',
            }
          )
        );
      }

    },

    fixWarning(warning) {
      if (this.$refs.widgetLayout) {
        this.$refs.widgetLayout.closeSetupChecklist();
      }

      this.mixinGuideToFix(warning, {
        active_tab_model: 'active_tab',
        panel_model: warning.location.tab === 0 ? 'expanded_general' : 'expanded_styles',
      });
    },

    onPageChange(page) {
      this.current_page = page;

      this.$nextTick(() => {
        if (this.$refs['preview-iframe']) {
          sendPageChangeToWidget(this.$refs['preview-iframe'], page);
        }
      });
    },

    onPreviewModeChange(mode) {
      this.preview_mode = mode;
    },

    onAutoFillTestData(recycle_data = false) {
      this.$nextTick(() => {
        if (this.$refs['preview-iframe']) {
          sendAutoFillTestData(this.$refs['preview-iframe'], recycle_data);
        }
      });
    },

    getDefaultEmailAgreementPattern() {
      if (this.form.email_consent_gathering_state === 'consent-via-field-submission') {
        return this.default_legal_patterns.email_agreement_implied;
      } else if (this.form.email_consent_gathering_state === 'checkbox-consent') {
        return this.default_legal_patterns.email_agreement_checkbox;
      }
      return this.default_legal_patterns.email_agreement_implied;
    },

    getDefaultPhoneAgreementPattern() {
      if (this.form.phone_number_consent_gathering_state === 'consent-via-field-submission') {
        return this.default_legal_patterns.phone_agreement_implied;
      } else if (this.form.phone_number_consent_gathering_state === 'checkbox-consent') {
        return this.default_legal_patterns.phone_agreement_checkbox;
      }
      return this.default_legal_patterns.phone_agreement_implied;
    },

    resetEmailAgreementToDefault() {
      const default_pattern = this.getDefaultEmailAgreementPattern();
      if (default_pattern) {
        this.form.email_consent_agreement_custom_patterns = [default_pattern];
        this.onChange();
      }
    },

    resetPhoneAgreementToDefault() {
      const default_pattern = this.getDefaultPhoneAgreementPattern();
      if (default_pattern) {
        this.form.phone_number_consent_agreement_custom_patterns = [default_pattern];
        this.onChange();
      }
    },

    get_default_double_opt_in_message_patterns() {
      return this.default_legal_patterns.double_opt_in_pattern
        ? [this.default_legal_patterns.double_opt_in_pattern]
        : ['Reply YES to confirm your subscription.'];
    },

    get_default_after_double_opt_in_message_patterns() {
      return this.default_legal_patterns.after_double_opt_in_message
        ? [this.default_legal_patterns.after_double_opt_in_message]
        : ["You're confirmed! Thanks for joining."];
    },

    get_default_no_double_opt_in_after_submission_message_patterns() {
      return this.default_legal_patterns.no_double_opt_in_after_submission_message
        ? [this.default_legal_patterns.no_double_opt_in_after_submission_message]
        : ["Thank you for confirming your order! You're now subscribed to our updates."];
    },

    loadDefaultLegalPatterns() {
      this.$rc.rest(
        'GET: /api/v1/order-confirmation/default-legal-patterns',
        {},
        data => {
          this.default_legal_patterns = {
            double_opt_in_pattern: data.double_opt_in_pattern || '',
            after_double_opt_in_message: data.after_double_opt_in_message || '',
            no_double_opt_in_after_submission_message: data.no_double_opt_in_after_submission_message || '',
            email_agreement_implied: data.email_agreement_implied || '',
            email_agreement_checkbox: data.email_agreement_checkbox || '',
            phone_agreement_implied: data.phone_agreement_implied || '',
            phone_agreement_checkbox: data.phone_agreement_checkbox || '',
          };
        },
        () => {
        }
      );
    },

    onEmailConsentGatheringChange() {
      this.onChange();
    },

    onPhoneConsentGatheringChange() {
      this.onChange();
    },

    getOrderConfAutomatedMessage() {
      this.$rc.rest({
          spinner: false,
          endpoint: 'PUT: /api/v1/auto-messages/org-profile',

          params : {
            organization_id: this.$route.params.organization_id,
          },

          on_success: (data) => {
            this.automated_message_template = data.templates.find(({ template_context }) => template_context === 'order-placed-request-order-confirmation') || null;
            this.uncreated_templates = data.uncreated_templates || []; // for the creation dialog if needed
          },
          on_error: 'Unable to list automated messages',
      });
    },

    suggestAutomatedMessageSetup() {
      // automated message exists but not linked to any form - proactively link the one they just published
      if (this.automated_message_template && this.automated_message_template.order_confirmation_settings_id === null) {
        this.linkAutomatedMessage('Form published successfully! Since an automated message already exists, it has now been linked to this form.', 7000);
        return;
      }

      // no automated message exists - suggest creating one
      if (this.has_necessary_integration && this.automated_message_does_not_exist) {
        this.$rc.confirm({  
          title: 'Create Automated Message?',
          body: 'Your order confirmation form has been published! The final step is to create an automated message to deliver this form to customers. Would you like to create it now?',
          btn_text_ok: 'Create Automated Message',
          btn_text_cancel: 'Maybe Later',
          on_ok: () => this.onClickCreateOrConfigureAutomatedMessage(),
        });
        return;
      }
      
      // automated message exists but linked to a different form - suggest linking to the one they just published
      if (this.has_necessary_integration && this.automated_message_exists_but_not_linked) {
        const body = `Your order confirmation form has been published! An automated message exists for Order Confirmations, but it is ${this.automated_message_template.order_confirmation_settings_id ? 'configured to use a different form' : 'not configured to use this form'}. Would you like to configure it to use this form${this.automated_message_template.order_confirmation_settings_id ? ' instead?' : '?'} `;
        const btn_text_ok = this.automated_message_template.order_confirmation_settings_id ? 'Use This Form Instead' : 'Use This Form';

        this.$rc.confirm({
          title: 'Update Automated Message?',
          body,
          btn_text_ok,
          btn_text_cancel: 'Maybe Later',
          on_ok: () => this.linkAutomatedMessage(),
        });
        return;
      }
      
      this.$rc.snackbar('Form published successfully!', 3000);
    },

    linkAutomatedMessage(snackbar_message = 'Automated message updated to use this form!', snackbar_timeout = 3000) {
      this.$rc.putAutoMessageTemplate(
        {
          ...this.automated_message_template,
          order_confirmation_settings_id: this.order_confirmation_settings_id,
        },

        (data) => {
          this.$rc.snackbar(snackbar_message, snackbar_timeout);
          setTimeout(() => {
            this.getOrderConfAutomatedMessage();
          }, 750);
          this.onChange();
        },

        () => {
          this.$rc.alert('Error', 'Unable to save template');
        },
      );
    },

    onClickCreateOrConfigureAutomatedMessage() {
      // if template already exists, open the settings dialog
      if (this.automated_message_template) {
        this.message_template_key++;
        this.show_automated_message_dialog = true;
        return;
      }

      // if template doesn't exist, show create dialog with preselected template
      if (!this.uncreated_templates) {
        return;
      }
      const order_conf_template = this.uncreated_templates.find(
        ut => ut.template_context === 'order-placed-request-order-confirmation'
      );
      if (!order_conf_template) {
        this.$rc.alert('Error', 'Order confirmation template type not found');
        return;
      }

      const preselected_choice = {
        text: order_conf_template.template_label,
        value: order_conf_template.template_context,
        template: order_conf_template,
      };

      this.$refs['create-new-dialog'].showDialog([preselected_choice], preselected_choice);
    },

    onConfirmNewTemplate(selected_template_choice) {
      this.onCreateNewTemplate(selected_template_choice);
    },

    onCreateNewTemplate(value) {
      const new_template = value.template;

      // link the template to this order confirmation form
      new_template.order_confirmation_settings_id = this.order_confirmation_settings_id;

      this.$rc.showSpinner();
      
      this.$rc.putAutoMessageTemplate(
        new_template,
      
        (data) => {
          this.$rc.hideSpinner();
          this.$rc.snackbar('Automated message created successfully!', 3000);
          this.getOrderConfAutomatedMessage();
          this.onChange();
        },
      
        () => {
          this.$rc.hideSpinner();
          this.$rc.alert('Error', 'Unable to create automated message');
        },
      );
    },
  },
};
</script>

<style scoped lang="scss">
.section-group {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.image-selector :deep(.v-input__slot) {
  margin-bottom: 0;
}
</style>
